import { BehaviorSubject } from 'rxjs';

import { ApiFragment } from '../../record/entities/api-fragment.entity';
import { Family, Metadata } from '../../record/entities/metadata.entity';
import { TaskSubject } from './task-subject.entity';

/**
 * The metadata a taak carries when it has no subject: every family present and empty. `Subject` is optional
 * on the wire, and the inherited getters read two levels deep without optional chaining, so such a taak
 * renders empty cells instead of throwing. A fresh object per taak, because metadata is written in place.
 */
function emptySubject(): Metadata {
    return Object.fromEntries(Object.values(Family).map(family => [family, {}])) as unknown as Metadata;
}

export class Task<K = string, V = TaskVariables> extends ApiFragment {
    constructor(public readonly task: BaseTask<Metadata, K, V>) {
        super(task.Subject ?? emptySubject());
    }

    getId(): string {
        return this.task.Id;
    }

    /**
     * A taak is tracked by its own id, not by the subject's. Its metadata *is* its subject, so the inherited
     * media-object id is the same for every taak on one dossier and the table would reuse one row's DOM.
     */
    trackBy(): string {
        return this.task.Id;
    }

    complete(payload?: any): TaskAction {
        return {
            Type: ActionType.COMPLETE,
            Payload: payload
        };
    }
}

export class TaskContainerData {
    constructor(public readonly task$: BehaviorSubject<Task>) {}
}

/**
 * The workflow variables a taak carries. The engine writes a different set per taaktype, so the shared type
 * is open and each page narrows it: edepot reads `EvaluationResult`, serieregister reads `EvaluationStatus`.
 */
export type TaskVariables = Record<string, any>;

export interface BaseTask<T extends TaskSubject | ApiFragment | Metadata, K = string, V = TaskVariables> {
    readonly Id: string;
    readonly Assignee: Assignee;
    readonly DueDate: string;
    readonly TaskInfo: {
        readonly Id: string;
        readonly Name: string;
        readonly Key: K;
    };
    readonly Subject?: T;
    readonly Completable: boolean;
    readonly Variables?: V;
    readonly Roles?: TaskRole[];
}

export interface Assignee {
    readonly Id: string;
    readonly Name: string;
}

/** The role a taak falls to when it has no named assignee. serieregister labels a row with it. */
export interface TaskRole {
    readonly Id: string;
    readonly Name: string;
}

export interface TaskAction {
    readonly Type: ActionType;
    readonly Payload?: any;
}

export enum ActionType {
    COMPLETE = 'COMPLETE',
    DELEGATE = 'DELEGATE'
}
